import { useEffect } from 'react';

import { GMetrixTableCellProps } from 'components/GMetrixTable/components';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { LicenseApi, LicenseSearchResult } from 'apis/LicenseApi';
import { ComboLicenseModal } from 'pages/licenses/combo_license';
import { helpContent } from 'pages/licenses/license_search/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageHeader } from 'components/layout/page';
import { GMetrixTable } from 'components';
import { Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';

type LicenseListProps = {
    onEdit: (id: number) => void;
    permissions: { canAddContent: boolean };
};

export const LicenseList = (props: LicenseListProps) => {

    const { onEdit, permissions } = props;

    const { formatMessage } = useIntl();

    const TYPE_OPTIONS = [
        { text: formatMessage({ id: 'all_types.6067f3c5', defaultMessage: 'All Types' }), value: 0 },
        { text: formatMessage({ id: 'site.84ff8857', defaultMessage: 'Site' }), value: 1 },
        { text: formatMessage({ id: 'individual.03bcdacf', defaultMessage: 'Individual' }), value: 2 },
        { text: formatMessage({ id: 'seat.42016b6c', defaultMessage: 'Seat' }), value: 3 }
    ];

    const [state, setState] = useSuperState({
        name: '',
        data: [] as LicenseSearchResult[],
        expire: '',
        loading: true,
        showModal: false,
        type: 0
    });

    const doSearch = async () => {

        try {

            setState({ loading: true });
            const data = await LicenseApi.Search(
                state.name,
                state.expire ? Number(state.expire) : 0,
                state.type
            );
            setState({ data, loading: false });

        } catch {

            setState({ loading: false });

        }

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        doSearch();

    }, []);

    type EditCellProps = GMetrixTableCellProps<LicenseSearchResult>;

    const EditCell = (editCellProps: EditCellProps) => {

        const { dataItem } = editCellProps;
        return (
            <td className="text-end">
                <GMetrixButton onClick={() => onEdit(dataItem.ID)} variant="primary">
                    <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                </GMetrixButton>
            </td>
        );

    };

    const showModal = () => setState({ showModal: true });

    const columns = [
        { field: 'ID', title: formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }), width: 80 },
        { field: 'Name', title: formatMessage({ id: 'license.506a29aa', defaultMessage: 'License' }) },
        { field: 'LicenseType', title: formatMessage({ id: 'type.a129b9e6', defaultMessage: 'Type' }), width: 120 },
        { field: 'LicenseDuration', title: formatMessage({ id: 'duration_days.c2b0a7a1', defaultMessage: 'Duration (days)' }), width: 140 },
        { field: 'DeliverableTypeString', title: formatMessage({ id: 'content.e305279d', defaultMessage: 'Content' }) },
        { field: 'DateCreate', title: formatMessage({ id: 'created.8d9f8791', defaultMessage: 'Created' }), width: 140 },
        { cell: EditCell, title: '', width: '1%' }
    ];

    return (
        <div className="licenses d-flex flex-column gap-3">
            <PageHeader
                actions={[
                    {
                        fillOnMobile: false,
                        label: formatMessage({ id: 'add_content.aad4180d', defaultMessage: 'Add Content' }),
                        to: '/licenses/content-management'
                    }
                ]}
                cta={{
                    label: (
                        <div className={'d-inline-flex align-items-center gap-2'}>
                            <i className={'fa-solid fa-plus'} />
                            <span>
                                <FormattedMessage defaultMessage="Add License" id="add_license.e6199c74" />
                            </span>
                        </div>
                    ),
                    onClick: showModal
                }}
                helpContent={helpContent}
                title={formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' })}
            />
            <Card body>
                <div className="d-flex flex-wrap gap-3 mb-4 align-items-end">
                    <div>
                        <label className="fw-bold d-block">
                            <FormattedMessage defaultMessage="Name" id="name.5458f379" />
                        </label>
                        <input
                            className="form-control"
                            onChange={e => setState({ name: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && doSearch()}
                            placeholder={formatMessage({ id: 'license_name.543aac3e', defaultMessage: 'License name' })}
                            type="text"
                            value={state.name}
                        />
                    </div>
                    <div>
                        <label className="fw-bold d-block">
                            <FormattedMessage defaultMessage="Expire (days)" id="expire_days.07298b4b" />
                        </label>
                        <input
                            className="form-control"
                            min={0}
                            onChange={e => setState({ expire: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && doSearch()}
                            type="number"
                            value={state.expire}
                        />
                    </div>
                    <div>
                        <label className="fw-bold d-block">
                            <FormattedMessage defaultMessage="Type" id="type.a129b9e6" />
                        </label>
                        <select
                            className="form-control"
                            onChange={e => setState({ type: Number(e.target.value) })}
                            value={state.type}
                        >
                            {TYPE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>
                                    {o.text}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex gap-2">
                        <GMetrixButton onClick={doSearch} variant="primary">
                            <FormattedMessage defaultMessage="Search" id="search.0ab06b2c" />
                        </GMetrixButton>
                        <GMetrixButton
                            onClick={() => setState({ name: '', expire: '', type: 0 })}
                            variant="outline-primary"
                        >
                            <FormattedMessage defaultMessage="Clear" id="clear.88d43eb1" />
                        </GMetrixButton>
                    </div>
                </div>
                {state.loading ? (
                    <GMetrixLoader />
                ) : (
                    <GMetrixTable
                        columnData={columns as any}
                        defaultItemsPerPage={20}
                        rowData={state.data}
                        tableName={formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' })}
                    />
                )}
            </Card>
            <ComboLicenseModal
                onClose={() => setState({ showModal: false })}
                onSaved={doSearch}
                show={state.showModal}
            />
        </div>
    );

};